import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth uses an httpOnly cookie set by the backend on login. The JWT is never
// stored in JS (localStorage), which protects it from XSS token theft.
export const api = axios.create({ baseURL: API, withCredentials: true });

// CSRF double-submit: echo the non-HttpOnly csrf_token cookie in a header on every
// state-changing request. Cross-site pages can neither read the cookie nor set this header.
function readCookie(name) {
  const m = document.cookie.match("(^|;)\\s*" + name + "\\s*=\\s*([^;]+)");
  return m ? decodeURIComponent(m.pop()) : "";
}
api.interceptors.request.use((config) => {
  const method = (config.method || "get").toUpperCase();
  if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
    const token = readCookie("csrf_token");
    // Fehlt das CSRF-Cookie, ist die Sitzung beendet (es hat dieselbe Lebensdauer wie das
    // Auth-Cookie). Der Request wird nicht abgeschickt, sondern sofort zur Anmeldung geleitet —
    // sonst bliebe die sensible Oberfläche nach einem 403 stehen.
    if (!token && !AUTH_PROBES.some((p) => (config.url || "").includes(p))) {
      endSession("expired");
      return Promise.reject(new axios.Cancel("session_expired"));
    }
    if (token) config.headers["X-CSRF-Token"] = token;
  }
  return config;
});

// Abgelaufene oder entzogene Sitzung: Sobald ein geschützter Request mit 401 (oder 403 wegen
// fehlendem CSRF-Token) antwortet, wird die Oberfläche NICHT weiter angezeigt, sondern sofort und
// einmalig zur Anmeldung geleitet. Ohne das blieben zuvor gerenderte (sensible) Inhalte sichtbar,
// obwohl die Sitzung nicht mehr gültig ist.
let sessionEnded = false;
const AUTH_PROBES = ["/auth/me", "/auth/login", "/auth/logout"];

export function endSession(reason = "expired") {
  if (sessionEnded) return;
  sessionEnded = true;
  const target = `/login?reason=${reason}`;
  if (window.location.pathname === "/login") return;
  window.location.replace(target);
}

const isSessionGone = (status, error) => {
  if (status === 401) return true;
  if (status !== 403) return false;
  const d = error?.response?.data?.detail;
  return /csrf/i.test(typeof d === "string" ? d : JSON.stringify(d || ""));
};

api.interceptors.response.use(
  (r) => r,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    if (isSessionGone(status, error) && !AUTH_PROBES.some((p) => url.includes(p))) {
      endSession("expired");
    }
    return Promise.reject(error);
  },
);

export function apiError(e, fallback = "Ein Fehler ist aufgetreten") {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || fallback;
  if (typeof d === "string") return d;
  if (typeof d === "object" && d.message) return d.message;
  if (Array.isArray(d)) return d.map((x) => (x.msg || JSON.stringify(x))
    // Pydantic stellt Validierungsmeldungen ein technisches Präfix voran.
    .replace(/^(Value|Type|Assertion) error,\s*/, "")).join(" ");
  return fallback;
}

// Locale-abhängige Präsentation. Wird vom I18nProvider gesetzt; verändert nie gespeicherte Werte.
let ACTIVE_INTL = "de-DE";
let ACTIVE_CURRENCY = "EUR";
export function setLocaleContext(intlTag, currency) {
  ACTIVE_INTL = intlTag || "de-DE";
  ACTIVE_CURRENCY = currency || "EUR";
}
export const activeCurrency = () => ACTIVE_CURRENCY;

export const eur = (n) =>
  new Intl.NumberFormat(ACTIVE_INTL, { style: "currency", currency: ACTIVE_CURRENCY }).format(Number(n || 0));

export const num = (n) => new Intl.NumberFormat(ACTIVE_INTL).format(Number(n || 0));

export const downloadFile = async (url, filename) => {
  const r = await api.get(url, { responseType: "blob" });
  const blobUrl = window.URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = blobUrl; a.download = filename; document.body.appendChild(a); a.click();
  a.remove(); window.URL.revokeObjectURL(blobUrl);
};

export const dateOnlyFmt = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString(ACTIVE_INTL, { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return s;
  }
};

export const dateFmt = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString(ACTIVE_INTL, { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return s;
  }
};
